import React, {memo, useCallback, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '@yosmanyga/di';
import {BufferList, Container, I18NText, Icon, Text, useIcons,} from '@yosmanyga/rich-ui';
import {RestorationListController} from '../../controller';
import {LogType} from '../../data';

const RestorationList = observer(({
    inverted,
}: {
    inverted: boolean
}) => {
    const restorationListController = useService(RestorationListController, []);

    useEffect(() => {
        (async () => {
            await restorationListController.load(inverted);
        })();
    }, []);

    const identifier = useCallback((item: LogType) => {
        return item.order.toString();
    }, []);

    const render = useCallback(
        (item: LogType) => {
            return <RenderItem
                item={item}
                selected={restorationListController.selected}
                onClick={() => {
                    restorationListController.select(item);
                }}
            />
        },
        [restorationListController.selected]
    );

    const onReached = useCallback(async () => {
        await restorationListController.more();
    }, []);

    if (!restorationListController.loaded) {
        return null;
    }

    if (restorationListController.logs.length === 0) {
        return <I18NText>No history yet</I18NText>;
    }

    return <BufferList<{ command: string; order: number }>
        height="100%"
        data={restorationListController.logs}
        init={5}
        identifier={identifier}
        change={restorationListController.selected}
        render={render}
        start={{
            first: restorationListController.first as number,
            top: (restorationListController.limit as number) - 1,
            onReached: onReached,
        }}
    />
});

const RenderItem = memo(
    ({
        item,
        selected,
        onClick,
    }: {
        item: LogType,
        selected?: LogType,
        onClick: () => void
    }) => {
        const icons = useIcons();

        const {command, order} = item;

        const target = typeof selected !== 'undefined' && order >= selected.order;

        return <Container
            key={order}
            intent='item'
            flow="row"
            align={{
                cross: 'center',
            }}
            opacity={
                target
                    ? 0.4
                    : undefined
            }
            onClick={onClick}
        >
            <Icon
                component={target ? icons.actions.remove : icons.states.ok}
                size='xxs'
            />
            <Text
                margin={{
                    left: 1,
                }}
            >
                {command}
            </Text>
        </Container>
    },
    (prev, next) => {
        if (typeof next.selected === 'undefined') {
            return true;
        }

        const order = next.item.order;
        const selected = next.selected.order;

        if (typeof prev.selected === 'undefined') {
            if (order < selected) {
                return true;
            }
        }
        // New selected is on top of current opacity?
        else if (selected < prev.selected.order) {
            if (order < selected || order >= prev.selected.order) {
                return true;
            }
        }
        // New selected is inside current opacity?
        else if (selected > prev.selected.order) {
            if (order < prev.selected.order || order >= selected) {
                return true;
            }
        }

        return false;
    }
);

export {RestorationList};
